import {
  createFileRoute,
  Outlet,
  Link,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  LayoutDashboard,
  FileText,
  Bot,
  Share2,
  Gauge,
  Mail,
  PlugZap,
  Wrench,
  Settings as SettingsIcon,
  Menu,
  LogOut,
  Loader2,
} from "lucide-react";

export const Route = createFileRoute("/admin")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin · Simply Approved Mortgages" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLayout,
});

const NAV: { to: string; label: string; icon: typeof LayoutDashboard; exact?: boolean }[] = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/posts", label: "Blog posts", icon: FileText },
  { to: "/admin/blog-agent", label: "AI blog agent", icon: Bot },
  { to: "/admin/chatbot", label: "AI chat bot", icon: Bot },
  { to: "/admin/social", label: "Social media", icon: Share2 },
  { to: "/admin/seo", label: "SEO operating system", icon: Gauge },
  { to: "/admin/emails", label: "Email tracking", icon: Mail },
  { to: "/admin/leads", label: "Leads", icon: Mail },
  { to: "/admin/integrations-health", label: "Integrations", icon: PlugZap },
  { to: "/admin/api-health", label: "API health monitor", icon: Gauge },
  { to: "/admin/optimal-blue", label: "Optimal Blue (beta)", icon: PlugZap },
  { to: "/admin/pricing-groups", label: "Public pricing groups", icon: Wrench },

  { to: "/admin/heloc-pricing", label: "HELOC pricing schedule", icon: Wrench },
  { to: "/admin/tools", label: "Loan officer tools", icon: Wrench },
  { to: "/admin/compliance", label: "Compliance", icon: SettingsIcon },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

function AdminLayout() {
  const { loading, session, user, roles, isStaff, roleError, signOut } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading && !session) {
      void navigate({ to: "/auth", search: { redirect: "/admin" }, replace: true });
    }
  }, [loading, session, navigate]);

  async function handleSignOut() {
    await signOut();
    await navigate({ to: "/auth", replace: true });
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking your access…
      </div>
    );
  }

  if (!session) {
    return (
      <div className="mx-auto max-w-lg px-4 py-32 text-center">
        <h1 className="text-2xl font-black">Opening sign in…</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Please sign in to open the admin dashboard.
        </p>
        <Button asChild className="mt-6">
          <Link to="/auth" search={{ redirect: "/admin" }}>
            Sign in
          </Link>
        </Button>
      </div>
    );
  }

  if (!isStaff) {
    return (
      <div className="mx-auto max-w-lg space-y-6 px-4 py-32 text-center">
        <h1 className="text-2xl font-black">Not authorized</h1>
        <p className="text-sm text-muted-foreground">
          Your account ({user?.email}) doesn't have staff access yet. Ask an administrator to grant
          you access, then sign in again.
        </p>
        {roleError ? (
          <Alert variant="destructive" className="text-left">
            <AlertTitle>Access check failed</AlertTitle>
            <AlertDescription>{roleError}</AlertDescription>
          </Alert>
        ) : null}
        <Button onClick={() => void handleSignOut()}>Sign out</Button>
      </div>
    );
  }

  const NavList = ({ onNavigate }: { onNavigate?: () => void }) => (
    <>
      <p className="px-2 pb-3 text-[11px] font-black uppercase tracking-widest text-brand">
        Staff portal
      </p>
      <nav className="space-y-1">
        {NAV.map(({ to, label, icon: Icon, exact }) => {
          const active =
            exact
              ? location.pathname === to
              : location.pathname === to || location.pathname.startsWith(`${to}/`);
          return (
            <Link
              key={to}
              to={to}
              onClick={onNavigate}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                active ? "bg-brand text-white" : "hover:bg-muted"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </Link>
          );
        })}
      </nav>
      <div className="mt-6 border-t border-border pt-4">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
        >
          View site ↗
        </Link>
        <p className="mt-2 truncate px-3 text-xs text-muted-foreground">
          {user?.email}
          {roles.length ? ` · ${roles.join(", ").replace(/_/g, " ")}` : ""}
        </p>
        <button
          type="button"
          onClick={() => void handleSignOut()}
          className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-muted"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="sticky top-0 z-30 flex items-center justify-between gap-2 border-b border-border bg-background/95 px-4 py-2 backdrop-blur lg:hidden">
        <Link to="/admin" className="text-[11px] font-black uppercase tracking-widest text-brand">
          Simply Approved Admin
        </Link>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="gap-2">
              <Menu className="h-4 w-4" /> Menu
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] overflow-y-auto p-4">
            <SheetTitle className="sr-only">Admin navigation</SheetTitle>
            <NavList onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>

      <div className="mx-auto grid w-full max-w-[1400px] gap-4 px-3 py-4 sm:px-6 lg:grid-cols-[248px_1fr] lg:py-8">
        <aside className="hidden self-start rounded-2xl border border-border bg-card p-4 lg:sticky lg:top-4 lg:block lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          <NavList />
        </aside>
        <main className="min-w-0 rounded-2xl border border-border bg-card p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
